import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SessionInfo } from "../../../shared/labels/contracts";
import { createLabelsApi, uiFault } from "./labels-api";
import type { UiFault } from "./ui-types";

export function useLabelsSessionWorkspace() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [fault, setFault] = useState<UiFault | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const pending = useRef<AbortController | null>(null);
  const bootstrapApi = useMemo(() => createLabelsApi(null), []);
  const api = useMemo(() => createLabelsApi(session?.csrfToken ?? null), [session?.csrfToken]);
  const refresh = useCallback(() => {
    if (!mounted.current) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const current = ++generation.current;
    const isCurrent = () => mounted.current && !controller.signal.aborted && current === generation.current;
    setLoading(true); setFault(null); setSession(null);
    void bootstrapApi.session(controller.signal).then(value => {
      if (isCurrent()) setSession(value);
    }).catch((error: unknown) => {
      if (isCurrent()) setFault(uiFault(error));
    }).finally(() => {
      if (isCurrent()) {pending.current = null; setLoading(false);}
    });
  }, [bootstrapApi]);
  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {mounted.current = false; generation.current++; pending.current?.abort(); pending.current = null;};
  }, [refresh]);
  return {session, loading, fault, api, refresh};
}
